import { Injectable } from '@angular/core';
import { CaseFormEntity } from '../../domain/entities/case-form.entity';
import { CaseFormDetailEntity } from '../../domain/entities/case-form-detail.entity';
import {
  CaseFormFieldEntity,
  CaseFormFieldType,
} from '../../domain/entities/case-form-field.entity';
import { CaseFormResponseDto } from '../dtos/case-form-response.dto';
import { CaseFormDetailResponseDto } from '../dtos/case-form-detail-response.dto';
import { CaseFormFieldDto } from '../dtos/case-form-field.dto';
import { UpdateCaseFormRequestDto } from '../dtos/update-case-form-request.dto';
import { UpdateCaseFormFieldDto } from '../dtos/update-case-form-field.dto';
import { FormVersionDetailResponseDto } from '../dtos/form-version-detail-response.dto';
import { FormVersionDetailRepositoryResponseDto } from '../dtos/form-version-detail-repository-response.dto';
import { FieldConstraintTypeResponseDto, FieldTypeResponseDto } from '../dtos/field-constraint-type-response.dto';

/**
 * Case Form Mapper
 * Converts between DTOs and domain entities
 */
@Injectable({ providedIn: 'root' })
export class CaseFormMapper {
  /**
   * Maps a case form response DTO to a domain entity
   */
  public toDomain(dto: CaseFormResponseDto): CaseFormEntity {
    const entity = new CaseFormEntity();
    const mappedStatus = this._mapStatus(dto.status);

    entity.id = dto.id;
    entity.name = dto.name;
    entity.description = dto.description ?? '';
    entity.caseTypeId = dto.caseTypeId;
    entity.caseTypeName = dto.caseTypeName;
    entity.status = mappedStatus;
    entity.statusName = mappedStatus;
    entity.publishedVersionId = dto.publishedVersionId;
    entity.publishedVersionNumber = dto.publishedVersionNumber;
    entity.fieldCount = dto.fieldCount;
    entity.createdAt = new Date(dto.createdOn);
    entity.updatedAt = new Date(dto.updatedOn);
    return entity;
  }

  /**
   * Maps status number to status string
   */
  private _mapStatus(status: number): 'Draft' | 'Published' | 'Archived' {
    switch (status) {
      case 0:
        return 'Draft';
      case 1:
        return 'Published';
      case 2:
        return 'Archived';
      default:
        return 'Draft';
    }
  }

  /**
   * Maps an array of case form response DTOs to domain entities
   */
  public toDomainArray(dtos: CaseFormResponseDto[]): CaseFormEntity[] {
    return dtos.map((dto) => this.toDomain(dto));
  }

  /**
   * Maps a case form detail response DTO to a domain entity
   */
  public toDetailDomain(dto: CaseFormDetailResponseDto): CaseFormDetailEntity {
    const entity = new CaseFormDetailEntity();
    entity.id = dto.id;
    entity.name = dto.name;
    entity.displayName = dto.display_name;
    entity.description = dto.description;
    entity.caseTypeId = dto.case_type_id;
    entity.caseTypeName = dto.case_type_name;
    entity.status = dto.status;
    entity.statusName = this._mapStatus(dto.status);
    entity.fields = dto.fields.map((fieldDto) => this.toFieldDomain(fieldDto));
    entity.createdAt = new Date(dto.created_at);
    entity.updatedAt = new Date(dto.updated_at);
    entity.createdBy = dto.created_by;
    entity.updatedBy = dto.updated_by;
    return entity;
  }

  /**
   * Maps a case form field DTO to a domain entity
   */
  private toFieldDomain(dto: CaseFormFieldDto): CaseFormFieldEntity {
    const entity = new CaseFormFieldEntity();
    entity.id = dto.id;
    entity.name = dto.name;
    entity.label = dto.label;
    entity.helperText = dto.helper_text;
    entity.placeholder = dto.placeholder;
    entity.fieldType = dto.field_type as CaseFormFieldType;
    entity.isRequired = dto.is_required;
    entity.isVisibleToRequester = dto.is_visible_to_requester;
    entity.isHiddenByDefault = dto.is_hidden_by_default;
    entity.sortOrder = dto.sort_order;
    entity.options = dto.options;
    entity.defaultValue = dto.default_value;
    entity.validationRules = dto.validation_rules;
    return entity;
  }

  /**
   * Maps update parameters to a request DTO
   */
  public toUpdateDto(params: UpdateCaseFormParams): UpdateCaseFormRequestDto {
    return {
      name: params.name,
      display_name: params.displayName,
      description: params.description,
      case_type_id: params.caseTypeId,
      is_active: params.isActive,
      fields: params.fields?.map((field) => this.toUpdateFieldDto(field)),
    };
  }

  /**
   * Maps update field parameters to a request DTO
   */
  private toUpdateFieldDto(params: UpdateCaseFormFieldParams): UpdateCaseFormFieldDto {
    return {
      id: params.id,
      name: params.name,
      label: params.label,
      field_type: params.fieldType,
      is_required: params.isRequired,
      sort_order: params.sortOrder,
      options: params.options,
      default_value: params.defaultValue,
      validation_rules: params.validationRules,
    };
  }

  /**
   * Maps form version detail response DTO to include statusName
   */
  public mapFormVersionDetail(dto: FormVersionDetailResponseDto): FormVersionDetailRepositoryResponseDto {
    const mappedStatus = this._mapStatus(dto.status);
    return {
      ...dto,
      statusName: mappedStatus,
    };
  }

  /**
   * Flattens nested field type constraints into legacy flat structure
   * Transforms: { fieldType, constraints: [...] } => [...{ id, fieldType, ... }]
   * @param fieldTypes Field types with nested constraints
   * @returns Flattened array of field constraint types
   */
  public static flattenFieldTypeConstraints(fieldTypes: FieldTypeResponseDto[]): FieldConstraintTypeResponseDto[] {
    const flattened: FieldConstraintTypeResponseDto[] = [];

    for (const fieldType of fieldTypes) {
      for (const constraint of fieldType.constraints) {
        // Convert defaultValue string to appropriate type based on valueType
        let defaultValue: string | number | boolean | null = constraint.defaultValue;

        if (constraint.defaultValue !== null) {
          switch (constraint.valueType) {
            case 'integer':
              defaultValue = parseInt(constraint.defaultValue, 10);
              break;
            case 'boolean':
              defaultValue = constraint.defaultValue === 'true';
              break;
            case 'string':
            case 'jsonb':
              defaultValue = constraint.defaultValue;
              break;
          }
        }

        flattened.push({
          id: constraint.typeConstraintId,
          fieldType: fieldType.fieldType,
          constraintKey: constraint.constraintKey,
          valueType: constraint.valueType,
          defaultValue,
          label: constraint.label,
          sortOrder: constraint.sortOrder,
        });
      }
    }

    return flattened;
  }
}

/**
 * Parameters for updating a case form
 */
export interface UpdateCaseFormParams {
  name?: string;
  displayName?: string;
  description?: string;
  caseTypeId?: string;
  isActive?: boolean;
  fields?: UpdateCaseFormFieldParams[];
}

/**
 * Parameters for updating a case form field
 */
export interface UpdateCaseFormFieldParams {
  id?: number;
  name: string;
  label: string;
  fieldType: string;
  isRequired: boolean;
  sortOrder: number;
  options?: string[];
  defaultValue?: string;
  validationRules?: Record<string, unknown>;
}
